import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { organizationAPI, userAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { countries, Country, getCountryByCode, getCountryByPhoneCode, allCurrencies, dateFormats, DateFormat } from '../../data/countries';

interface Organisation {
  id: number;
  name: string;
  website: string;
  industry: string;
  size: string;
  address: string;
  phone: string;
  country: string;
  currency: string;
  date_format: DateFormat;
  created_at: string;
}

interface UserProfile {
  id: number;
  name: string;
  email: string;
  country: string;
  country_code: string;
  mobile: string;
}

const OrganisationProfilePage: React.FC = () => {
  useDocumentTitle('Organisation Profile');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'organisation' | 'profile'>('organisation');
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [selectedOrgCountry, setSelectedOrgCountry] = useState<Country | null>(null);
  const [selectedProfileCountry, setSelectedProfileCountry] = useState<Country | null>(null);

  const [orgForm, setOrgForm] = useState({
    name: '',
    website: '',
    industry: '',
    size: '',
    address: '',
    phone: '',
    country: '',
    currency: '',
    date_format: 'DD-MM-YYYY' as DateFormat,
  });

  const [profileForm, setProfileForm] = useState({
    name: '',
    country: '',
    country_code: '',
    mobile: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [orgResponse, profileResponse] = await Promise.all([
        organizationAPI.getCurrent(),
        userAPI.getProfile(),
      ]);

      if (orgResponse.data.status === 'success') {
        const orgData = orgResponse.data.data;
        setOrganisation(orgData);

        // Set selected country for organization
        if (orgData.country) {
          const country = getCountryByCode(orgData.country);
          setSelectedOrgCountry(country || null);
        }

        setOrgForm({
          name: orgData.name || '',
          website: orgData.website || '',
          industry: orgData.industry || '',
          size: orgData.size || '',
          address: orgData.address || '',
          phone: orgData.phone || '',
          country: orgData.country || '',
          currency: orgData.currency || '',
          date_format: orgData.date_format || 'DD-MM-YYYY',
        });
      }

      if (profileResponse.data.status === 'success') {
        const profileData = profileResponse.data.data;
        setProfile(profileData);

        // Set selected country for profile
        if (profileData.country) {
          const country = getCountryByCode(profileData.country);
          setSelectedProfileCountry(country || null);
        } else if (profileData.country_code) {
          // Try to find country by phone code if country not set
          const country = getCountryByPhoneCode(profileData.country_code);
          setSelectedProfileCountry(country || null);
        }

        setProfileForm({
          name: profileData.name || '',
          country: profileData.country || '',
          country_code: profileData.country_code || '',
          mobile: profileData.mobile || '',
        });
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load profile data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOrgCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    const country = getCountryByCode(countryCode);

    if (country) {
      setSelectedOrgCountry(country);
      setOrgForm(prev => ({
        ...prev,
        country: countryCode,
        currency: country.currency,
        date_format: country.dateFormat,
      }));
    } else {
      setSelectedOrgCountry(null);
      setOrgForm(prev => ({
        ...prev,
        country: '',
      }));
    }
  };

  const handleProfileCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    const country = getCountryByCode(countryCode);

    if (country) {
      setSelectedProfileCountry(country);
      setProfileForm(prev => ({
        ...prev,
        country: countryCode,
        country_code: country.phoneCode,
      }));
    } else {
      setSelectedProfileCountry(null);
      setProfileForm(prev => ({
        ...prev,
        country: '',
        country_code: '',
      }));
    }
  };

  const handleSaveOrganisation = async () => {
    if (!organisation) return;

    setSaving(true);
    try {
      await organizationAPI.update(organisation.id, orgForm);
      toast.success('Organisation updated successfully');
      loadData();
    } catch (error: any) {
      toast.error('Failed to update organisation');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await userAPI.updateProfile(profileForm);
      if (response.data.status === 'success') {
        toast.success('Profile updated successfully');
        loadData();
      }
    } catch (error: any) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'education', label: 'Education' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'media', label: 'Media & Entertainment' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'other', label: 'Other' },
  ];

  const sizes = [
    { value: 'startup', label: 'Startup (1-10)' },
    { value: 'small_business', label: 'Small Business (11-50)' },
    { value: 'medium_business', label: 'Medium Business (51-200)' },
    { value: 'enterprise', label: 'Enterprise (200+)' },
    { value: 'freelancer', label: 'Freelancer' },
    { value: 'agency', label: 'Agency' },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading profile...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="mb-4">
          <h1 className="h3 mb-1">Settings</h1>
          <p className="text-muted mb-0">Manage your organisation and personal profile</p>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'organisation' ? 'active' : ''}`}
              onClick={() => setActiveTab('organisation')}
            >
              <i className="bi bi-building me-2"></i>Organisation
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <i className="bi bi-person me-2"></i>My Profile
            </button>
          </li>
        </ul>

        {/* Organisation Tab */}
        {activeTab === 'organisation' && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0">Organisation Details</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Organisation Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={orgForm.name}
                        onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Website</label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://example.com"
                        value={orgForm.website}
                        onChange={(e) => setOrgForm({ ...orgForm, website: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Industry</label>
                      <select
                        className="form-select"
                        value={orgForm.industry}
                        onChange={(e) => setOrgForm({ ...orgForm, industry: e.target.value })}
                      >
                        <option value="">Select Industry</option>
                        {industries.map(ind => (
                          <option key={ind.value} value={ind.value}>{ind.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Organisation Size</label>
                      <select
                        className="form-select"
                        value={orgForm.size}
                        onChange={(e) => setOrgForm({ ...orgForm, size: e.target.value })}
                      >
                        <option value="">Select Size</option>
                        {sizes.map(size => (
                          <option key={size.value} value={size.value}>{size.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Country</label>
                      <select
                        className="form-select"
                        value={orgForm.country}
                        onChange={handleOrgCountryChange}
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Currency</label>
                      <select
                        className="form-select"
                        value={orgForm.currency}
                        onChange={(e) => setOrgForm({ ...orgForm, currency: e.target.value })}
                      >
                        <option value="">Select Currency</option>
                        {allCurrencies.map(curr => (
                          <option key={curr.code} value={curr.code}>
                            {curr.code} - {curr.name}
                          </option>
                        ))}
                      </select>
                      {selectedOrgCountry && orgForm.currency === selectedOrgCountry.currency && (
                        <div className="form-text text-success">
                          <i className="bi bi-check-circle me-1"></i>
                          Auto-selected based on country
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Date Format</label>
                      <select
                        className="form-select"
                        value={orgForm.date_format}
                        onChange={(e) => setOrgForm({ ...orgForm, date_format: e.target.value as DateFormat })}
                      >
                        {dateFormats.map(df => (
                          <option key={df.value} value={df.value}>
                            {df.label} (e.g. {df.example})
                          </option>
                        ))}
                      </select>
                      {selectedOrgCountry && orgForm.date_format === selectedOrgCountry.dateFormat && (
                        <div className="form-text text-success">
                          <i className="bi bi-check-circle me-1"></i>
                          Auto-selected based on country
                        </div>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Business address"
                        value={orgForm.address}
                        onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ minWidth: '90px' }}>
                          {selectedOrgCountry ? (
                            <>
                              {selectedOrgCountry.flag} {selectedOrgCountry.phoneCode}
                            </>
                          ) : (
                            <span className="text-muted">+--</span>
                          )}
                        </span>
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="Phone number"
                          value={orgForm.phone}
                          onChange={(e) => setOrgForm({ ...orgForm, phone: e.target.value.replace(/\D/g, '') })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer bg-white border-top py-3">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveOrganisation}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0">Organisation Info</h5>
                </div>
                <div className="card-body">
                  <div className="text-center mb-4">
                    <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px', fontSize: '2rem' }}>
                      {orgForm.name?.charAt(0)?.toUpperCase() || 'O'}
                    </div>
                    <h5 className="mb-1">{orgForm.name || 'Organisation'}</h5>
                    <p className="text-muted small mb-0">{orgForm.website || 'No website'}</p>
                  </div>
                  <hr />
                  <div className="mb-3">
                    <small className="text-muted">Created</small>
                    <div>{organisation?.created_at ? new Date(organisation.created_at).toLocaleDateString() : '-'}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Country</small>
                    <div>
                      {selectedOrgCountry ? (
                        <>{selectedOrgCountry.flag} {selectedOrgCountry.name}</>
                      ) : '-'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Currency</small>
                    <div>
                      {orgForm.currency ? (
                        <>
                          {orgForm.currency} - {allCurrencies.find(c => c.code === orgForm.currency)?.name || ''}
                        </>
                      ) : '-'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Date Format</small>
                    <div>
                      {orgForm.date_format || 'DD-MM-YYYY'}
                    </div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Industry</small>
                    <div className="text-capitalize">{orgForm.industry?.replace(/_/g, ' ') || '-'}</div>
                  </div>
                  <div>
                    <small className="text-muted">Size</small>
                    <div className="text-capitalize">{orgForm.size?.replace(/_/g, ' ') || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="row">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0">Personal Information</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className="form-control"
                        value={profile?.email || ''}
                        disabled
                      />
                      <div className="form-text">Email cannot be changed</div>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Country</label>
                      <select
                        className="form-select"
                        value={profileForm.country}
                        onChange={handleProfileCountryChange}
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Phone Number</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ minWidth: '90px' }}>
                          {selectedProfileCountry ? (
                            <>
                              {selectedProfileCountry.flag} {selectedProfileCountry.phoneCode}
                            </>
                          ) : (
                            <span className="text-muted">+--</span>
                          )}
                        </span>
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="Mobile number"
                          value={profileForm.mobile}
                          onChange={(e) => setProfileForm({ ...profileForm, mobile: e.target.value.replace(/\D/g, '') })}
                          disabled={!profileForm.country}
                        />
                      </div>
                      {!profileForm.country && (
                        <div className="form-text text-muted">
                          Select a country first to enter phone number
                        </div>
                      )}
                      {selectedProfileCountry && profileForm.country_code === selectedProfileCountry.phoneCode && (
                        <div className="form-text text-success">
                          <i className="bi bi-check-circle me-1"></i>
                          Phone code auto-selected based on country
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-footer bg-white border-top py-3">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveProfile}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>Save Profile
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0">Profile Summary</h5>
                </div>
                <div className="card-body text-center">
                  <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px', fontSize: '2rem' }}>
                    {profileForm.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <h5 className="mb-1">{profileForm.name || 'User'}</h5>
                  <p className="text-muted small mb-2">{profile?.email}</p>
                  {selectedProfileCountry && (
                    <p className="text-muted small mb-3">
                      {selectedProfileCountry.flag} {selectedProfileCountry.name}
                    </p>
                  )}
                  <span className="badge bg-primary">Organisation Owner</span>
                </div>
              </div>

              <div className="card border-0 shadow-sm mt-4">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0">Security</h5>
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-3">
                    Your account uses passwordless authentication via email OTP.
                  </p>
                  <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
                    <div>
                      <i className="bi bi-shield-check text-success me-2"></i>
                      <span className="small">OTP Authentication</span>
                    </div>
                    <span className="badge bg-success">Enabled</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrganisationProfilePage;
